import * as React from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import {
  FieldValues,
  FormProvider,
  useForm,
  UseFormReturn,
  UseFormSetError
} from "react-hook-form";
import { AnyObjectSchema } from "yup";
import { FormInstance, setErrorsFromResponse } from "duck";

interface FormProps<TValues extends FieldValues>
  extends Omit<BoxProps, "onSubmit" | "children"> {
  validationSchema?: AnyObjectSchema;
  defaultValues?: Record<string, any>;
  onSubmit?(
    values: TValues,
    setError: UseFormSetError<TValues>
  ): Promise<any> | void;
  children:
    | ((
        props: UseFormReturn<TValues> & { submit: () => Promise<any> }
      ) => React.ReactNode)
    | React.ReactNode;
  innerRef?: React.RefObject<FormInstance<TValues>>;
}

const Form = <TValues extends FieldValues = FieldValues>({
  validationSchema,
  defaultValues,
  onSubmit,
  children,
  innerRef,
  ...rest
}: FormProps<TValues>): React.ReactElement => {
  const formProviderProps = useForm<TValues>({
    defaultValues: defaultValues as any,
    ...(validationSchema && {
      resolver: (yupResolver as any)(validationSchema)
    }),
    mode: "onBlur"
  });

  const handleSubmit = formProviderProps.handleSubmit((data: any) => {
    if (!onSubmit) {
      return Promise.resolve();
    }
    const submitResult = onSubmit(data, formProviderProps.setError);
    if (submitResult) {
      return submitResult.catch(error =>
        setErrorsFromResponse(error, formProviderProps.setError)
      );
    }
    return Promise.resolve();
  });

  React.useImperativeHandle(
    innerRef,
    () => ({ ...formProviderProps, submit: handleSubmit }),
    [formProviderProps, handleSubmit]
  );

  return (
    <FormProvider {...(formProviderProps as any)}>
      <Box as="form" noValidate onSubmit={handleSubmit} {...rest}>
        {typeof children === "function"
          ? children({ ...formProviderProps, submit: handleSubmit })
          : children}
      </Box>
    </FormProvider>
  );
};

export default Form;
